
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChargingStation } from '@/types/task';
import { initialStations } from '@/data/stations';
import StationHeader from '@/components/station/StationHeader';
import StationImage from '@/components/station/StationImage';
import StationInfo from '@/components/station/StationInfo';
import HostInfo from '@/components/station/HostInfo';
import BookingSidebar from '@/components/station/BookingSidebar';
import StationDetailsSkeleton from '@/components/station/StationDetailsSkeleton';
import ErrorBoundary from '@/components/ErrorBoundary';
import AIImageGenerator from '@/components/station/AIImageGenerator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const StationDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [station, setStation] = useState<ChargingStation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStation = async () => {
      setIsLoading(true);
      
      try {
        const { data: supabaseStation, error } = await supabase
          .from('stations')
          .select('*')
          .eq('id', id!)
          .single();
          
        if (supabaseStation && !error) {
          const chargerType = validateChargerType(supabaseStation.chargerType);
          
          // Process coordinates safely with type checking
          let stationCoordinates = { latitude: 0, longitude: 0 };
          if (supabaseStation.coordinates && typeof supabaseStation.coordinates === 'object' && !Array.isArray(supabaseStation.coordinates)) {
            // Handle potential different formats for coordinates
            const coords = supabaseStation.coordinates as Record<string, any>;
            stationCoordinates = {
              latitude: typeof coords.latitude === 'number' ? coords.latitude : 
                       typeof coords.lat === 'number' ? coords.lat : 0,
              longitude: typeof coords.longitude === 'number' ? coords.longitude : 
                        typeof coords.lng === 'number' ? coords.lng : 0
            };
          }
          
          const fullStation: ChargingStation = {
            ...supabaseStation,
            rating: supabaseStation.rating || 0,
            reviews: [],
            images: initialStations[0].images,
            photos: initialStations[0].photos,
            pricePerKwh: supabaseStation.price || 0,
            checkins: 0,
            description: 'No description available',
            coordinates: stationCoordinates,
            powerOutput: supabaseStation.powerOutput || 50,
            amenities: [],
            plugTypes: supabaseStation.plugTypes || [],
            paymentTypes: supabaseStation.paymentTypes || [],
            chargerType: chargerType,
            isHomeCharger: !!supabaseStation.isHomeCharger,
            availableHours: supabaseStation.availableHours || undefined,
            networkOperator: supabaseStation.networkOperator || undefined,
            numberOfPorts: supabaseStation.numberOfPorts || undefined,
            parkingType: supabaseStation.parkingType || undefined,
            status: supabaseStation.status || undefined,
            verified: !!supabaseStation.verified
          };
          
          setStation(fullStation);
        } else {
          const foundStation = initialStations.find(s => s.id === id);
          if (foundStation) {
            foundStation.reviews = foundStation.reviews || [];
          }
          setStation(foundStation || null);
        }
      } catch (error) {
        console.error("Error fetching station:", error);
        const foundStation = initialStations.find(s => s.id === id);
        setStation(foundStation || null);
        toast({
          title: 'Error loading station',
          description: 'Using cached data instead',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStation();
  }, [id, toast]);

  const validateChargerType = (type: string): 'fast' | 'standard' | 'ultra-fast' => {
    switch (type.toLowerCase()) {
      case 'fast':
        return 'fast';
      case 'ultra-fast':
      case 'ultrafast':
      case 'ultra':
        return 'ultra-fast';
      default:
        return 'standard';
    }
  };

  const handleImageGenerated = async (imageUrl: string) => {
    if (station) {
      setStation({ ...station, aiGeneratedImage: imageUrl });
    }
  };

  if (isLoading) {
    return <StationDetailsSkeleton />;
  }

  if (!station) {
    return (
      <div className="container py-8 text-center">
        <h1 className="text-2xl font-bold">Station not found</h1>
        <p className="mt-4">The charging station you're looking for doesn't exist.</p>
        <StationHeader />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container max-w-6xl py-8">
        <StationHeader />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <StationImage 
              imageUrl={station.photos?.[0]} 
              aiGeneratedImage={station.aiGeneratedImage}
              title={station.title}
              isHomeCharger={station.isHomeCharger}
            />
            
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">AI-Generated Station Image</h3>
              <AIImageGenerator
                stationId={station.id}
                stationData={station}
                existingImageUrl={station.aiGeneratedImage}
                onImageGenerated={handleImageGenerated}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Images are generated using AI based on station details. Results may vary.
              </p>
            </div>

            <StationInfo
              title={station.title}
              location={station.location}
              rating={station.rating}
              reviews={station.reviews}
              powerOutput={station.powerOutput}
              available={station.available}
              chargerType={station.chargerType}
              availableHours={station.availableHours}
              isHomeCharger={!!station.isHomeCharger}
              amenities={station.amenities}
            />

            <HostInfo 
              hostName={station.hostName} 
              isHomeCharger={!!station.isHomeCharger} 
            />
          </div>

          <div>
            <BookingSidebar 
              price={station.price}
              powerOutput={station.powerOutput}
              chargerType={station.chargerType}
              available={station.available}
              stationId={station.id}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default StationDetails;
